<script setup lang="ts" generic="T">
import { computed, nextTick, onBeforeUnmount, ref, shallowRef } from 'vue'

const props = defineProps<{
  modelValue: T[]
  itemKey: (item: T) => string | number
  disabled?: boolean
}>()

const emit = defineEmits<{ 'update:modelValue': [T[]] }>()

// Two root nodes (list + teleported clone), so fallthrough attrs must be bound by hand.
defineOptions({ inheritAttrs: false })

const DRAG_THRESHOLD = 5
const SWAP_OVERLAP_THRESHOLD = 0.45
const REORDER_MS = 220
const REORDER_EASING = 'cubic-bezier(0.2, 0, 0, 1)'

const container = ref<HTMLElement | null>(null)
const draggingKey = ref<string | number | null>(null)
const clone = shallowRef<{ x: number; y: number; width: number; height: number } | null>(null)
const settling = ref(false)

const items = computed(() => props.modelValue)

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

interface DragState {
  key: string | number
  startX: number
  startY: number
  pointerX: number
  pointerY: number
  rect: DOMRect
  active: boolean
  frame: number
  order: T[]
}

let drag: DragState | null = null
let suppressClick = false

function itemElements(): HTMLElement[] {
  if (!container.value) return []
  return Array.from(container.value.querySelectorAll<HTMLElement>('[data-sortable-item]'))
}

function elementFor(key: string | number): HTMLElement | undefined {
  return itemElements().find((el) => el.dataset.sortableItem === String(key))
}

async function animateReorder(mutate: () => void) {
  if (reducedMotion()) {
    mutate()
    return
  }

  const tracked = itemElements().filter((el) => el.dataset.sortableItem !== String(draggingKey.value))
  const before = new Map(tracked.map((el) => [el.dataset.sortableItem, el.getBoundingClientRect()]))

  mutate()
  await nextTick()

  for (const el of itemElements()) {
    const first = before.get(el.dataset.sortableItem)
    if (!first) continue
    const last = el.getBoundingClientRect()
    const dx = first.left - last.left
    const dy = first.top - last.top
    if (!dx && !dy) continue

    el.style.transition = 'none'
    el.style.transform = `translate(${dx}px, ${dy}px)`
  }

  void container.value?.offsetHeight

  for (const el of itemElements()) {
    if (!el.style.transform) continue
    el.style.transition = `transform ${REORDER_MS}ms ${REORDER_EASING}`
    el.style.transform = ''
    el.addEventListener(
      'transitionend',
      () => {
        el.style.transition = ''
      },
      { once: true },
    )
  }
}

function overlappedIndex(rect: { left: number; right: number; top: number; bottom: number }) {
  let best: { index: number; rect: DOMRect } | null = null
  let bestRatio = SWAP_OVERLAP_THRESHOLD

  itemElements().forEach((el, index) => {
    if (el.dataset.sortableItem === String(draggingKey.value)) return
    const r = el.getBoundingClientRect()
    const overlapX = Math.max(0, Math.min(rect.right, r.right) - Math.max(rect.left, r.left))
    const overlapY = Math.max(0, Math.min(rect.bottom, r.bottom) - Math.max(rect.top, r.top))
    const ratio = (overlapX * overlapY) / (r.width * r.height)
    if (ratio > bestRatio) {
      bestRatio = ratio
      best = { index, rect: r }
    }
  })

  return best as { index: number; rect: DOMRect } | null
}

function renderFrame() {
  if (!drag || settling.value) return
  drag.frame = 0

  const dx = drag.pointerX - drag.startX
  const dy = drag.pointerY - drag.startY
  clone.value = {
    x: drag.rect.left + dx,
    y: drag.rect.top + dy,
    width: drag.rect.width,
    height: drag.rect.height,
  }

  const moved = {
    left: drag.rect.left + dx,
    right: drag.rect.right + dx,
    top: drag.rect.top + dy,
    bottom: drag.rect.bottom + dy,
  }
  const target = overlappedIndex(moved)
  if (!target) return

  const current = drag.order.findIndex((item) => props.itemKey(item) === drag!.key)
  if (current === -1 || current === target.index) return

  const midX = moved.left + drag.rect.width / 2
  const midY = moved.top + drag.rect.height / 2
  const targetMidX = target.rect.left + target.rect.width / 2
  const targetMidY = target.rect.top + target.rect.height / 2
  const before = Math.abs(midY - targetMidY) < target.rect.height / 2 ? midX < targetMidX : midY < targetMidY

  let next = before ? target.index : target.index + 1
  if (current < next) next -= 1
  if (next === current) return

  animateReorder(() => {
    const order = [...drag!.order]
    const [moving] = order.splice(current, 1)
    order.splice(next, 0, moving)
    drag!.order = order
    emit('update:modelValue', order)
  })
}

function onPointerMove(event: PointerEvent) {
  if (!drag || settling.value) return
  drag.pointerX = event.clientX
  drag.pointerY = event.clientY

  if (!drag.active) {
    if (
      Math.abs(event.clientX - drag.startX) < DRAG_THRESHOLD &&
      Math.abs(event.clientY - drag.startY) < DRAG_THRESHOLD
    ) {
      return
    }
    startDrag()
  }

  if (!drag.frame) drag.frame = requestAnimationFrame(renderFrame)
}

function startDrag() {
  if (!drag) return
  drag.active = true
  draggingKey.value = drag.key
  suppressClick = true
  clone.value = {
    x: drag.rect.left,
    y: drag.rect.top,
    width: drag.rect.width,
    height: drag.rect.height,
  }
}

async function onPointerUp() {
  if (!drag) return
  if (!drag.active) {
    endDrag()
    return
  }

  if (drag.frame) {
    cancelAnimationFrame(drag.frame)
    renderFrame()
  }

  const slot = elementFor(drag.key)?.getBoundingClientRect()
  if (!slot || reducedMotion()) {
    finishDrag()
    return
  }

  settling.value = true
  clone.value = { x: slot.left, y: slot.top, width: slot.width, height: slot.height }
  window.setTimeout(finishDrag, REORDER_MS)
}

function finishDrag() {
  settling.value = false
  draggingKey.value = null
  clone.value = null
  endDrag()
}

function endDrag() {
  if (drag?.frame) cancelAnimationFrame(drag.frame)
  drag = null
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
  window.removeEventListener('pointercancel', onPointerUp)
}

function onPointerDown(event: PointerEvent) {
  if (props.disabled || drag || event.button !== 0 || !event.isPrimary) return

  const el = (event.target as HTMLElement).closest<HTMLElement>('[data-sortable-item]')
  if (!el || !container.value?.contains(el)) return
  if ((event.target as HTMLElement).closest('button, a, input, select, textarea')) return

  const key = el.dataset.sortableItem
  const index = items.value.findIndex((item) => String(props.itemKey(item)) === key)
  if (index === -1) return

  suppressClick = false
  const rect = el.getBoundingClientRect()
  drag = {
    key: props.itemKey(items.value[index]),
    startX: event.clientX,
    startY: event.clientY,
    pointerX: event.clientX,
    pointerY: event.clientY,
    rect,
    active: false,
    frame: 0,
    order: [...items.value],
  }

  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup', onPointerUp)
  window.addEventListener('pointercancel', onPointerUp)
}

function onClickCapture(event: MouseEvent) {
  if (!suppressClick) return
  suppressClick = false
  event.stopPropagation()
  event.preventDefault()
}

const draggedItem = computed(() =>
  draggingKey.value === null ? null : (items.value.find((item) => props.itemKey(item) === draggingKey.value) ?? null),
)

onBeforeUnmount(endDrag)
</script>

<template>
  <div ref="container" v-bind="$attrs" @pointerdown="onPointerDown" @click.capture="onClickCapture">
    <div
      v-for="(item, index) in items"
      :key="itemKey(item)"
      :data-sortable-item="String(itemKey(item))"
      class="touch-none"
      :class="[disabled ? 'cursor-default' : 'cursor-grab', draggingKey === itemKey(item) && 'invisible']"
    >
      <slot :item="item" :index="index" />
    </div>
  </div>

  <Teleport to="body">
    <div
      v-if="clone && draggedItem"
      class="pointer-events-none fixed top-0 left-0 z-50 origin-top-left cursor-grabbing drop-shadow-lg"
      :style="{
        translate: `${clone.x}px ${clone.y}px`,
        width: `${clone.width}px`,
        transition: settling ? `translate ${REORDER_MS}ms ${REORDER_EASING}, scale 120ms ease` : 'scale 120ms ease',
        scale: settling ? '1' : '1.04',
      }"
    >
      <slot :item="draggedItem" :index="-1" />
    </div>
  </Teleport>
</template>
